<script setup lang="ts">
import type { CmsElementTestApplicationForm } from "~/composables/useCustom";
import { onClickOutside } from "@vueuse/core";
import { ApiClientError } from "@shopware/api-client";
import type { ApiError } from "@shopware/api-client";
import { defu } from "defu";
import { useVuelidate } from "@vuelidate/core";
import { customValidators } from "@/i18n/utils/i18n-validators";
const { required, minLength, email } = customValidators();

const props = defineProps<{
  content: CmsElementTestApplicationForm;
}>();

const {
  Boxes,
  boxLink,
  headlineText,
  headlineType,
  mainText,
  rightHeadlineText,
} = props.content.data;

type Translations = {
  form: {
    salutation: string;
    salutationPlaceholder: string;
    firstName: string;
    firstNamePlaceholder: string;
    lastName: string;
    lastNamePlaceholder: string;
    email: string;
    emailPlaceholder: string;
    phone: string;
    phonePlaceholder: string;
    subject: string;
    subjectPlaceholder: string;
    comment: string;
    commentPlaceholder: string;
    privacy: string;
    dataProtection: string;
    submit: string;
    messages: {
      contactFormSuccess: string;
    };
  };
};

let translations: Translations = {
  form: {
    salutation: "Salutation",
    salutationPlaceholder: "Enter salutation...",
    firstName: "First name",
    firstNamePlaceholder: "Enter first name...",
    lastName: "Last name",
    lastNamePlaceholder: "Enter last name...",
    email: "Email address",
    emailPlaceholder: "Enter email address...",
    phone: "Phone number",
    phonePlaceholder: "Enter phone number...",
    subject: "Subject",
    subjectPlaceholder: "Enter subject...",
    comment: "Comment",
    commentPlaceholder: "Enter comment...",
    privacy: "Privacy",
    dataProtection: "I have read the data protection information.",
    submit: "Submit",
    messages: {
      contactFormSuccess:
        "We have received your contact request and will process it as soon as possible.",
    },
  },
};

translations = defu(useCmsTranslations(), translations) as Translations;

const loading = ref<boolean>();
const formSent = ref<boolean>(false);
const { t } = useI18n();
type ErrorMessages = ApiClientError<{
  errors: ApiError[];
}>;

const errorMessages = ref<ErrorMessages[]>([]);

const { getSalutations } = useSalutations();
const { foreignKey } = useNavigationContext();
const { apiClient } = useShopwareContext();

const state = reactive({
  company: "",
  salutationId: "",
  firstName: "",
  lastName: "",
  email: "",
  subject: t("form.inquiry"),
  comment: "",
  phone: "",
  checkbox: true,
});

const rules = computed(() => ({
  email: {
    required,
    email,
  },
  company: {
    required,
  },
  firstName: {
    required,
    minLength: minLength(3),
  },
  lastName: {
    required,
    minLength: minLength(3),
  },
  salutationId: {
    required,
  },
  phone: {
    minLength: minLength(1),
  },
  checkbox: {
    required,
    isTrue: (value: boolean) => value === true,
  },
}));

const $v = useVuelidate(rules, state);
const invokeSubmit = async () => {
  $v.value.$touch();
  const valid = await $v.value.$validate();
  console.log(valid);
  if (valid) {
    loading.value = true;
    if (state.company) {
      state.comment += "\n" + t("form.company") + ": " + state.company;
    }
    try {
      state.phone = state.phone || "-";
      await apiClient.invoke("sendContactMail post /contact-form", {
        body: {
          ...state,
          navigationId: foreignKey.value,
        },
      });
      formSent.value = true;
    } catch (e) {
      if (e instanceof ApiClientError) {
        errorMessages.value = e.details.errors;
      }
    } finally {
      state.phone = "";
      loading.value = false;
    }
  }
};

watch(getSalutations, (newValue) => {
  state.salutationId = newValue[newValue.length - 1].id;
});
</script>
<template>
  <div class="cms-element-test-application-form prose-p:text-sm">
    <div class="container">
      <div
        class="flex -mx-4 laptop:-mx-7 flex-wrap flex-col gap-y-10 md:flex-row"
      >
        <div class="w-full px-4 laptop:px-7 md:w-2/4">
          <h2 class="section-title mb-7" v-if="headlineText">
            {{ headlineText }}
          </h2>
          <div v-if="mainText" v-html="mainText"></div>

          <form
            v-if="!formSent"
            class="flex flex-col max-w-full mx-auto relative test"
            @submit.prevent="invokeSubmit"
          >
            <div
              v-if="loading"
              class="absolute inset-0 flex items-center justify-center z-10 bg-white/50"
            >
              <div
                class="h-15 w-15 i-carbon-progress-bar-round animate-spin c-gray-500"
              />
            </div>
            <div class="grid grid-cols-12 gap-5">
              <div class="col-span-12">
                <input
                  id="first-name"
                  v-model="state.company"
                  name="first-name"
                  type="text"
                  :placeholder="$t('form.company') + ' *'"
                  autocomplete="first-name"
                  class="w-full h-11 text-sm !border-skin-primary bg-[transparent] rounded-[0.2rem] !ring-transparent"
                  :class="[
                    $v.company.$error
                      ? 'border-red-600 focus:border-red-600'
                      : 'border-skin-primary focus:border-skin-primary',
                  ]"
                  @blur="$v.company.$touch()"
                />
                <span
                  v-if="$v.company.$error"
                  class="pt-1 text-sm text-red-600 focus:ring-brand-primary border-skin-primary"
                >
                  {{ $v.company.$errors[0].$message }}
                </span>
              </div>
              <div class="col-span-12 hidden">
                <!--<label for="salutation">{{ $t('form.salutation') }} *</label>-->
                <select
                  id="salutation"
                  v-model="state.salutationId"
                  name="salutation"
                  class="input-sty bg-no-repeat text-[14px] pt-[10px] h-11 bg-select-arrow w-full rounded-[3px] py-2 px-5 focus:ring-gray-300"
                  :class="[
                    $v.salutationId.$error
                      ? 'border-red-600 focus:border-red-600'
                      : 'border-[#D9D9D9] focus:border-skin-primary',
                  ]"
                  @blur="$v.salutationId.$touch()"
                >
                  <option value="" disabled selected>
                    {{ $t("form.salutation") }} *
                  </option>
                  <option
                    v-for="salutation in getSalutations"
                    :key="salutation.id"
                    :value="salutation.id"
                  >
                    {{ salutation.displayName }}
                  </option>
                </select>
                <span
                  v-if="$v.salutationId.$error"
                  class="pt-1 text-sm text-red-600 focus:ring-brand-primary border-skin-primary"
                >
                  {{ $v.salutationId.$errors[0].$message }}
                </span>
              </div>
              <div class="col-span-12 md:col-span-6">
                <!--<label for="first-name">{{ $t('form.firstName') }} *</label>-->
                <input
                  id="first-name"
                  v-model="state.firstName"
                  name="first-name"
                  type="text"
                  :placeholder="$t('form.firstName') + ' *'"
                  autocomplete="first-name"
                  class="w-full h-11 text-sm !border-skin-primary bg-[transparent] rounded-[0.2rem] !ring-transparent"
                  :class="[
                    $v.firstName.$error
                      ? 'border-red-600 focus:border-red-600'
                      : 'border-skin-primary focus:border-skin-primary',
                  ]"
                  @blur="$v.firstName.$touch()"
                />
                <span
                  v-if="$v.firstName.$error"
                  class="pt-1 text-sm text-red-600 focus:ring-brand-primary border-skin-primary"
                >
                  {{ $v.firstName.$errors[0].$message }}
                </span>
              </div>
              <div class="col-span-12 md:col-span-6">
                <!--<label for="last-name">{{ $t('form.lastName') }} *</label>-->
                <input
                  id="last-name"
                  v-model="state.lastName"
                  name="last-name"
                  type="text"
                  :placeholder="$t('form.lastName') + ' *'"
                  autocomplete="last-name"
                  class="w-full h-11 text-sm !border-skin-primary bg-[transparent] rounded-[0.2rem] !ring-transparent"
                  :class="[
                    $v.lastName.$error
                      ? 'border-red-600 focus:border-red-600'
                      : 'border-skin-primary focus:border-skin-primary',
                  ]"
                  @blur="$v.lastName.$touch()"
                />
                <span
                  v-if="$v.lastName.$error"
                  class="pt-1 text-sm text-red-600 focus:ring-brand-primary pt-[10px] border-skin-primary"
                >
                  {{ $v.lastName.$errors[0].$message }}
                </span>
              </div>
              <div class="col-span-12">
                <input
                  id="email-address"
                  v-model="state.email"
                  name="email"
                  type="email"
                  :placeholder="$t('form.email') + ' *'"
                  autocomplete="email"
                  :class="[
                    $v.email.$error
                      ? 'border-red-600 focus:border-red-600'
                      : 'border-skin-primary focus:border-skin-primary',
                  ]"
                  class="w-full h-11 text-sm !border-skin-primary bg-[transparent] rounded-[0.2rem] !ring-transparent"
                  @blur="$v.email.$touch()"
                />
                <span
                  v-if="$v.email.$error"
                  class="pt-1 text-sm text-red-600 focus:ring-brand-primary border-skin-primary"
                >
                  {{ $v.email.$errors[0].$message }}
                </span>
              </div>
              <div class="col-span-12">
                <!--<label for="phone">{{ $t('form.phone') }} *</label>-->
                <input
                  id="phone"
                  v-model="state.phone"
                  name="phone"
                  type="text"
                  :placeholder="$t('form.phonePlaceHolder')"
                  autocomplete="phone"
                  class="w-full h-11 text-sm !border-skin-primary bg-[transparent] rounded-[0.2rem] !ring-transparent"
                  :class="[
                    $v.phone.$error
                      ? 'border-red-600 focus:border-red-600'
                      : 'border-skin-primary focus:border-skin-primary',
                  ]"
                  @blur="$v.phone.$touch()"
                />
                <span
                  v-if="$v.phone.$error"
                  class="pt-1 text-sm text-red-600 focus:ring-brand-primary border-skin-primary"
                >
                  {{ $v.phone.$errors[0].$message }}
                </span>
              </div>

              <div class="col-span-12">
                <!--<label for="comment">{{ $t('form.comment') }} *</label>-->
                <textarea
                  id="comment"
                  v-model="state.comment"
                  name="comment"
                  type="text"
                  :placeholder="$t('form.inquiry')"
                  autocomplete="comment"
                  class="appearance-none bg-[transparent] relative block w-full py-2 text-[14px] pt-[10px] border-skin-primary focus:border-skin-primary border text-gray-900 rounded-[3px] focus:outline-none focus:ring-gray-300 focus:z-10 sm:text-sm"
                  rows="5"
                />
              </div>
              <div class="col-span-12 mt-5">
                <hr />
                <p class="text-[14px] text-skin-secondary leading-5">
                  {{ $t("form.formMandatoryContent") }}
                </p>
              </div>
              <div class="col-span-12 mt-5">
                <button class="btn w-full" type="submit">
                  {{ $t("form.submit") }}
                </button>
              </div>
            </div>
          </form>
          <template v-else>
            <div
              class="flex items-center justify-center !text-lg font-bold text-center min-h-[77%]"
            >
              <i>{{ $t("form.inquirySuccess") }}</i>
            </div>
          </template>
        </div>
        <div class="w-full px-4 laptop:px-7 md:w-2/4">
          <h2 class="section-title mb-7" v-if="rightHeadlineText">
            {{ rightHeadlineText }}
          </h2>
          <div
            class="grid grid-cols-1 gap-5 prose-p:text-skin-white prose-p:font-bold prose-p:text-sm mb-7"
          >
            <div v-for="box in Boxes" :key="box.id">
              <div class="bg-skin-theme rounded-10 p-5">
                <i class="block w-6 h-6 mb-5" v-if="box.boxImage">
                  <NuxtImg
                    :src="box.boxImage.media.url"
                    :alt="box.boxImage.media.name"
                    height="24"
                    width="24"
                  />
                </i>
                <div v-html="box.boxText"></div>
              </div>
            </div>
          </div>
          <div class="text-center md:text-left">
            <SharedButton
              v-if="boxLink"
              :button="boxLink"
              :class="['btn-outline w-full sm:w-auto']"
              :has-arrow="true"
            ></SharedButton>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
